import { DynamicFormFieldModel } from 'src/app/@shared/form/dynamic-form-field/dynamic-form-field.model';
import { FormFieldModel } from 'src/app/@shared/form/form-field/form-field.model';
import { FormFieldValue } from 'src/app/@shared/form/form.types';
import { File as ProtoFile } from '@api/file/@shared/file.entity';
import { SubItemEntry } from 'src/app/@shared/detail/detail-list-item/detail-list-item.component';
import { getDisplayNames } from 'src/app/@shared/form/form-helper';

const ORGANIZATION_RELATION_KEYS = new Set([
  'branchOffices',
  'contactOrganization',
  'localContactPoint',
  'mainOffice',
  'providedByOther',
  'providedByRed',
  'provider',
  'providesFor',
]);

const TOPIC_RELATION_KEYS = new Set(['interestedInfos', 'topics']);

type LinkableEntity = {
  id?: string;
  subType?: string;
  type?: string;
};

function getEntityHref(itemId: string, value: LinkableEntity): string {
  if (!value?.id) {
    return '';
  }

  if (itemId === 'person' || value.type === 'person') {
    return `/main/persons/detail/${value.id}`;
  }

  if (TOPIC_RELATION_KEYS.has(itemId) || value.type === 'topic') {
    return `/main/topics/detail/${value.id}`;
  }

  if (!ORGANIZATION_RELATION_KEYS.has(itemId) && !value?.subType) {
    return '';
  }

  if (!value.type) {
    return '';
  }

  return `/main/organizations/${value.type}/${value.subType || 'all'}/detail/${value.id}`;
}

function getTranslatableValues(item: FormFieldModel | DynamicFormFieldModel,
  value: FormFieldValue | FormFieldValue[]): SubItemEntry[] {
  const values = Array.isArray(value)
    ? value
    : [value];

  if (!item.options?.length) {
    console.warn('no translation found -> use raw values');

    return values.map((v, index) => {
      return {
        id: `${item.id}_${index}`,
        value: v.toString(),
      };
    });
  }

  return item.options.filter((option) => {
    return values.includes(option.value);
  }).map((option, index) => {
    return {
      id: `${item.id}_${index}`,
      value: option.label,
    };
  });
}

// eslint-disable-next-line max-lines-per-function
export function getFormattedEntries(item: FormFieldModel | DynamicFormFieldModel, value: any): SubItemEntry[] {
  let formattedEntries: SubItemEntry[] = [];

  if (value === null || typeof value === 'undefined') {
    return formattedEntries;
  }

  if (item.inputType === 'toggle' || typeof value === 'boolean') {
    const formattedValue = value === true
      ? 'TERM.YES'
      : 'TERM.NO';

    return [
      {
        id: item.id,
        value: formattedValue,
      },
    ];
  }

  if (!value) {
    return formattedEntries;
  }

  if (item.inputType === 'file') {
    const fileValue = value as ProtoFile;

    return [
      {
        id: fileValue.id,
        label: fileValue.name,
        value: fileValue.publicUrl,
      },
    ];
  }

  const values = Array.isArray(value)
    ? value
    : [value];

  switch (item.valueType) {
    case 'translatable':
      formattedEntries = getTranslatableValues(item, values);
      break;

    case 'date':
      formattedEntries = values.map((v: FormFieldValue, index) => {
        return {
          id: `${item.id}_${index}`,
          value: new Date(v as string).toLocaleDateString(),
        };
      });
      break;

    case 'entityArray':
      formattedEntries = values.map((v, index) => {
        const linkableEntity = v as LinkableEntity;

        return {
          href: getEntityHref(item.id, linkableEntity),
          id: v.id || `${item.id}_${index}`,
          value: getDisplayNames(v, true)[0],
        };
      });
      break;

    default:
      formattedEntries = values.map((v, index) => {
        return {
          id: `${item.id}_${index}`,
          value: v.toString(),
        };
      });
  }

  return formattedEntries.filter((entry) => {
    return entry && typeof entry.value !== 'undefined' && entry.value !== null && entry.value !== '';
  });
}
